/** The twelve steps of onboarding, in order. Keys match the API's completeness report. */
export interface WizardStep {
  key: string;
  title: string;
  /** One line saying what this step is for, shown under the heading. */
  blurb: string;
  /**
   * Nothing on this step is needed to submit, so the API's completeness
   * report has no entry for it and the stepper never shows it as owed.
   */
  optional?: boolean;
}

export const WIZARD_STEPS: WizardStep[] = [
  { key: "personal", title: "Personal details", blurb: "Your name and date of birth as they appear on your Emirates ID." },
  { key: "contact", title: "Contact details", blurb: "How HR reaches you, and where you live." },
  { key: "emergency", title: "Emergency contact", blurb: "Who we call if something happens at work." },
  {
    key: "nomination",
    title: "Nomination",
    blurb: "Who should be contacted, and who you nominate, if the worst happens.",
  },
  {
    key: "employment",
    title: "Employment",
    blurb: "Your employee number, your role, your departments and the day you started.",
  },
  { key: "emirates-id", title: "Emirates ID", blurb: "Your card number, its dates, and a copy of both sides." },
  { key: "passport-visa", title: "Passport and visa", blurb: "Your passport and UAE residency details." },
  {
    key: "labour-insurance",
    title: "Labour card and insurance",
    blurb: "Your labour card and your health insurance card, with a copy of each.",
  },
  { key: "contract-pay", title: "Contract and pay", blurb: "Your contract dates, salary and bank account." },
  { key: "hours", title: "Working hours", blurb: "Check these match your contract. Overtime is worked out from them." },
  { key: "education", title: "Education and papers", blurb: "Your qualification, your CV and your attested degree." },
  { key: "signature", title: "Signature", blurb: "Sign once here and reuse it on documents HR sends you." },
];

export function stepIndex(key: string): number {
  return WIZARD_STEPS.findIndex((step) => step.key === key);
}

export function nextStepKey(key: string): string | null {
  const index = stepIndex(key);
  return index >= 0 && index < WIZARD_STEPS.length - 1
    ? WIZARD_STEPS[index + 1]!.key
    : null;
}

export function previousStepKey(key: string): string | null {
  const index = stepIndex(key);
  return index > 0 ? WIZARD_STEPS[index - 1]!.key : null;
}

/** Which step owns a given field, so the review screen can link to it. */
export const STEP_BY_FIELD: Record<string, string> = {
  full_name_en: "personal",
  full_name_ar: "personal",
  gender: "personal",
  date_of_birth: "personal",
  nationality: "personal",
  marital_status: "personal",
  profile_picture_file_id: "personal",

  personal_mobile: "contact",
  whatsapp_number: "contact",
  personal_email: "contact",
  emirate_of_residence: "contact",
  full_address: "contact",

  emergency_contact_name: "emergency",
  emergency_contact_relationship: "emergency",
  emergency_contact_mobile: "emergency",

  nominee_name: "nomination",
  nominee_relationship: "nomination",
  nominee_nationality: "nomination",
  nominee_mobile: "nomination",
  nominee_address: "nomination",

  employee_id: "employment",
  departments: "employment",
  position_title: "employment",
  date_of_joining: "employment",

  emirates_id_number: "emirates-id",
  emirates_id_issue: "emirates-id",
  emirates_id_expiry: "emirates-id",
  emirates_id_front_file_id: "emirates-id",
  emirates_id_back_file_id: "emirates-id",

  passport_number: "passport-visa",
  passport_issue: "passport-visa",
  passport_expiry: "passport-visa",
  passport_file_id: "passport-visa",
  visa_type: "passport-visa",
  visa_number: "passport-visa",
  visa_issue: "passport-visa",
  visa_expiry: "passport-visa",
  visa_file_id: "passport-visa",

  labour_card_number: "labour-insurance",
  labour_card_issue: "labour-insurance",
  labour_card_expiry: "labour-insurance",
  labour_card_file_id: "labour-insurance",
  health_insurance_provider: "labour-insurance",
  health_insurance_number: "labour-insurance",
  health_insurance_issue: "labour-insurance",
  health_insurance_expiry: "labour-insurance",
  health_insurance_file_id: "labour-insurance",

  contract_start: "contract-pay",
  contract_expiry: "contract-pay",
  contract_file_id: "contract-pay",
  basic_salary: "contract-pay",
  allowances: "contract-pay",
  bank_name: "contract-pay",
  iban: "contract-pay",
  account_holder_name: "contract-pay",
  iban_letter_file_id: "contract-pay",

  hours_per_day: "hours",
  weekly_off_days: "hours",

  highest_education: "education",
  cv_file_id: "education",
  degree_file_id: "education",
  experience_letter_file_id: "education",
  other_file_id: "education",

  signature_file_id: "signature",
  declaration_accepted_at: "signature",
};
